import React from "react"
import Script from "react-load-script"

class DynamicMap3D extends React.Component {
    constructor(props) {
        super(props)
        this.mapRef = React.createRef()
        this.state = {
            mapsJSLoaded: false,
            map3DInitialized: false,
            selectedPointIndex: -1,
            cameraFollowMode: false,
            currentCornerIndex: 0,
        }

        // Store references to 3D elements
        this.polylineElement = null
        this.markerElements = []
        this.map3DElement = null
        this.groundLevelAltitude = null // Cache actual ground level from Google Maps
    }

    componentDidMount() {
        this.mapElem = this.mapRef.current
        this.setState({ mapMounted: true })
    }

    componentWillUnmount() {
        this.cleanup3DElements()
    }

    cleanup3DElements() {
        if (this.polylineElement && this.polylineElement.parentNode) {
            this.polylineElement.parentNode.removeChild(this.polylineElement)
            this.polylineElement = null
        }

        this.markerElements.forEach((marker) => {
            if (marker.parentNode) {
                marker.parentNode.removeChild(marker)
            }
        })
        this.markerElements = []

        // Clean up any colored segments
        if (this.mapElem) {
            const segments = this.mapElem.querySelectorAll(
                ".racing-line-segment"
            )
            segments.forEach((segment) => {
                if (segment.parentNode) {
                    segment.parentNode.removeChild(segment)
                }
            })
        }
    }

    componentDidUpdate(prevProps) {
        if (
            !this.state.map3DInitialized &&
            this.state.mapMounted &&
            this.state.mapsJSLoaded &&
            this.props.lat &&
            this.props.lng
        ) {
            this.initMap3D()
            return
        }

        if (this.state.map3DInitialized) {
            const selectionChanged =
                JSON.stringify(this.props.selectedRange) !==
                JSON.stringify(prevProps.selectedRange)
            const selectedPointChanged =
                this.props.selectedPointIndex !== prevProps.selectedPointIndex

            // Update 3D elements when points change
            const pointsChanged =
                this.props.points?.length !== prevProps.points?.length ||
                JSON.stringify(this.props.points) !==
                    JSON.stringify(prevProps.points)

            if (pointsChanged) {
                console.log("Points changed, updating 3D elements:", {
                    newCount: this.props.points?.length || 0,
                    oldCount: prevProps.points?.length || 0,
                })

                // Get actual ground level first, then update 3D elements
                this.getActualGroundLevel(() => {
                    setTimeout(() => {
                        this.update3DLine()
                        this.update3DMarkers()
                    }, 50)
                })
            }

            if (selectionChanged || selectedPointChanged) {
                this.updateColoredSegments()
                const pointIndex = this.props.selectedPointIndex
                if (
                    Number.isInteger(pointIndex) &&
                    this.props.points?.[pointIndex]
                ) {
                    this.flyToPoint(pointIndex)
                }
            }

            // Update camera position if center changes
            if (
                this.props.lat !== prevProps.lat ||
                this.props.lng !== prevProps.lng
            ) {
                this.updateCameraPosition()
            }

            // Update zoom if changed
            if (this.props.zoom !== prevProps.zoom) {
                this.updateCameraRange()
            }
        }
    }

    initMap3D() {
        const map3D = this.mapElem
        this.map3DElement = map3D

        // Ensure we have valid coordinates
        const lat = parseFloat(this.props.lat)
        const lng = parseFloat(this.props.lng)

        if (isNaN(lat) || isNaN(lng)) {
            console.error("Invalid coordinates for 3D map:", { lat, lng })
            return
        }

        // Set up 3D map properties
        map3D.center = { lat, lng, altitude: 0 }
        map3D.range = 1000
        map3D.tilt = 45
        map3D.heading = 0
        map3D.mode = "SATELLITE"

        // Add event listeners
        map3D.addEventListener("gmp-click", this.handle3DMapClick.bind(this))
        map3D.addEventListener(
            "gmp-camera-changed",
            this.handleCameraChange.bind(this)
        )
        map3D.addEventListener("gmp-error", (error) => {
            console.error("Google Maps 3D rendering failed:", error)
            if (this.props.onLoadError) {
                this.props.onLoadError(error)
            }
        })

        // Listen for when the map is ready
        map3D.addEventListener("gmp-ready", () => {
            console.log("3D Map ready event fired")
            if (this.props.onReady) {
                this.props.onReady()
            }
            if (this.props.points && this.props.points.length > 0) {
                // First get the actual ground level altitude, then update the 3D elements
                this.getActualGroundLevel(() => {
                    setTimeout(() => {
                        this.update3DLine()
                        this.update3DMarkers()
                    }, 100)
                })
            }
        })

        this.setState({ map3DInitialized: true })
        console.log("3D Map initialized with coordinates:", { lat, lng })

        // Wait for the 3D map to be fully ready before adding elements
        const waitForMapReady = () => {
            console.log(
                "Map ready check - Points available:",
                this.props.points?.length || 0
            )

            if (this.props.points && this.props.points.length > 0) {
                console.log(
                    "Map is ready, adding racing line with points:",
                    this.props.points.length
                )
                // Get actual ground level first, then update 3D elements
                this.getActualGroundLevel(() => {
                    this.update3DLine()
                    this.update3DMarkers()
                })
            } else {
                console.log("No points to render yet")
            }
        }

        // Give the 3D map more time to initialize
        setTimeout(waitForMapReady, 500)
    }

    handle3DMapClick = (event) => {
        if (this.props.readOnly) return

        console.log("3D Map clicked:", event.detail)
        const position = event.detail.position
        if (!position) return

        const { lat, lng, altitude } = position
        const parsedLat = parseFloat(lat)
        const parsedLng = parseFloat(lng)
        const parsedAltitude = parseFloat(altitude) || 0

        // Validate coordinates
        if (isNaN(parsedLat) || isNaN(parsedLng)) {
            console.warn("Invalid coordinates from 3D click:", {
                lat,
                lng,
                altitude,
            })
            return
        }

        // Check if we're clicking near an existing point
        const clickedPointIndex = this.props.points?.findIndex((point) => {
            const latDiff = Math.abs(parseFloat(point.lat) - parsedLat)
            const lngDiff = Math.abs(parseFloat(point.lng) - parsedLng)
            return latDiff < 0.00005 && lngDiff < 0.00005
        })

        if (clickedPointIndex !== -1) {
            // Point clicked - select it
            this.setState({ selectedPointIndex: clickedPointIndex })
            if (this.props.onPointSelect) {
                this.props.onPointSelect(clickedPointIndex)
            }
        } else {
            // Empty space clicked - add new point
            const points = [...(this.props.points || [])]

            // Check if we're near the first point to close the line
            if (points.length > 2) {
                const firstPoint = points[0]
                const latDiff = Math.abs(parseFloat(firstPoint.lat) - parsedLat)
                const lngDiff = Math.abs(parseFloat(firstPoint.lng) - parsedLng)
                const isNearFirstPoint = latDiff < 0.00005 && lngDiff < 0.00005

                if (isNearFirstPoint) {
                    points.push({
                        lat: firstPoint.lat,
                        lng: firstPoint.lng,
                        altitude: parseFloat(firstPoint.altitude) || 0,
                        type: this.props.pointType,
                    })
                } else {
                    points.push({
                        lat: parsedLat,
                        lng: parsedLng,
                        altitude: parsedAltitude,
                        type: this.props.pointType,
                    })
                }
            } else {
                points.push({
                    lat: parsedLat,
                    lng: parsedLng,
                    altitude: parsedAltitude,
                    type: this.props.pointType,
                })
            }

            console.log("Adding new point:", points[points.length - 1])
            this.props.addPoint(points)

            // Select the newly added point
            const newIndex = points.length - 1
            this.setState({ selectedPointIndex: newIndex })
            if (this.props.onPointSelect) {
                this.props.onPointSelect(newIndex)
            }
        }
    }

    handleCameraChange = (event) => {
        // Update parent component with camera position for saving
        if (this.props.setCamera) {
            const { center, range, tilt, heading } = event.detail
            this.props.setCamera({ center, range, tilt, heading })
        }
    }

    update3DLine() {
        console.log(
            "update3DLine called with points:",
            this.props.points?.length || 0
        )

        if (
            !this.mapElem ||
            !this.props.points ||
            this.props.points.length < 2
        ) {
            console.log("Not enough points or no map element")
            if (this.polylineElement && this.polylineElement.parentNode) {
                this.polylineElement.parentNode.removeChild(
                    this.polylineElement
                )
                this.polylineElement = null
            }
            return
        }

        // Clean up existing elements first
        if (this.polylineElement && this.polylineElement.parentNode) {
            this.polylineElement.parentNode.removeChild(this.polylineElement)
            this.polylineElement = null
        }

        try {
            // Check if we're in a browser environment and Google Maps 3D is available
            if (
                typeof window === "undefined" ||
                !window.google ||
                !window.google.maps
            ) {
                console.error("Google Maps API not available")
                return
            }

            // Create new 3D polyline element
            this.polylineElement = document.createElement("gmp-polyline-3d")
            console.log("Created 3D polyline element:", this.polylineElement)

            // Convert points to the required format with altitude defaulting to 0
            let coordinates = this.props.points.map((point, index) => {
                const lat = parseFloat(point.lat)
                const lng = parseFloat(point.lng)
                const altitude = parseFloat(point.altitude) || 0

                if (index === 0) {
                    console.log("First point coordinates:", {
                        lat,
                        lng,
                        altitude,
                        original: point,
                    })
                }

                return { lat, lng, altitude }
            })

            // Apply smoothing if we have enough points (similar to 2D version)
            if (
                !this.props.preserveMeasuredGeometry &&
                coordinates.length > 4
            ) {
                coordinates = this.smoothCoordinates(coordinates)
                console.log(
                    "Applied smoothing, new coordinate count:",
                    coordinates.length
                )
            }

            // Validate coordinates
            const validCoordinates = coordinates.filter(
                (coord) =>
                    !isNaN(coord.lat) &&
                    !isNaN(coord.lng) &&
                    coord.lat >= -90 &&
                    coord.lat <= 90 &&
                    coord.lng >= -180 &&
                    coord.lng <= 180
            )

            console.log(
                "Valid coordinates for 3D line:",
                validCoordinates.length,
                "out of",
                coordinates.length
            )
            console.log("Sample coordinates:", validCoordinates.slice(0, 3))

            if (validCoordinates.length < 2) {
                console.warn(
                    "Not enough valid coordinates for 3D line:",
                    validCoordinates
                )
                return
            }

            // Set properties first
            this.polylineElement.strokeColor = "#70bd74"
            this.polylineElement.strokeWidth = 8 // Restored original width
            // Don't set altitudeMode - let it use the default
            // this.polylineElement.altitudeMode = 'relativeToGround';
            this.polylineElement.extruded = true

            console.log("3D polyline properties set:", {
                strokeColor: this.polylineElement.strokeColor,
                strokeWidth: this.polylineElement.strokeWidth,
                extruded: this.polylineElement.extruded,
            })

            // Try to discover valid altitude modes
            console.log(
                "Polyline element properties:",
                Object.getOwnPropertyNames(this.polylineElement)
            )
            if (this.polylineElement.constructor) {
                console.log(
                    "Polyline constructor:",
                    this.polylineElement.constructor.name
                )
            }

            // Append to map first, then set coordinates
            this.mapElem.appendChild(this.polylineElement)
            console.log("3D polyline appended to map element")

            // Set coordinates after appending to DOM
            this.polylineElement.coordinates = validCoordinates
            console.log(
                "3D polyline coordinates set:",
                validCoordinates.length,
                "points"
            )

            // Add click handler
            this.polylineElement.addEventListener("gmp-click", (event) => {
                if (!this.props.readOnly) {
                    console.log("3D Polyline clicked:", event.detail)
                }
            })

            // Verify the element is in the DOM
            console.log(
                "3D polyline in DOM:",
                this.mapElem.contains(this.polylineElement)
            )
            console.log("Map element children:", this.mapElem.children.length)

            // Create colored segments after a short delay
            setTimeout(() => {
                this.updateColoredSegments()
            }, 200)
        } catch (error) {
            console.error("Error creating 3D polyline:", error)
            console.error("Error stack:", error.stack)
        }
    }

    updateColoredSegments() {
        if (!this.mapElem || !this.props.points || this.props.points.length < 2)
            return

        // Remove existing colored segments
        const existingSegments = this.mapElem.querySelectorAll(
            ".racing-line-segment"
        )
        existingSegments.forEach((segment) => {
            if (segment.parentNode) {
                segment.parentNode.removeChild(segment)
            }
        })

        try {
            // Convert original points to coordinates format for smoothing
            let originalCoordinates = this.props.points.map(
                (point, pointIndex) => ({
                    lat: parseFloat(point.lat),
                    lng: parseFloat(point.lng),
                    altitude: parseFloat(point.altitude) || 0,
                    type: point.type,
                    distanceM: point.distanceM,
                    pointIndex,
                })
            )

            // Apply the same smoothing as the main line
            let smoothedCoordinates = originalCoordinates
            if (
                !this.props.preserveMeasuredGeometry &&
                originalCoordinates.length > 4
            ) {
                const rawSmoothed = this.smoothCoordinates(originalCoordinates)

                // Map smoothed coordinates back to original point types
                smoothedCoordinates = rawSmoothed.map((coord) => {
                    // Find the closest original point to determine type
                    let closestPointIndex = 0
                    let minDistance = Infinity

                    originalCoordinates.forEach((origPoint, origIndex) => {
                        const distance =
                            Math.abs(origPoint.lat - coord.lat) +
                            Math.abs(origPoint.lng - coord.lng)
                        if (distance < minDistance) {
                            minDistance = distance
                            closestPointIndex = origIndex
                        }
                    })

                    return {
                        ...coord,
                        type:
                            originalCoordinates[closestPointIndex]?.type ||
                            "acc",
                        distanceM:
                            originalCoordinates[closestPointIndex]?.distanceM,
                        pointIndex:
                            originalCoordinates[closestPointIndex]?.pointIndex,
                    }
                })
            }

            // Create colored segments based on smoothed points
            for (let i = 1; i < smoothedCoordinates.length; i++) {
                const currentPoint = smoothedCoordinates[i]
                const prevPoint = smoothedCoordinates[i - 1]

                if (!currentPoint.type) continue

                const segment = document.createElement("gmp-polyline-3d")
                segment.className = "racing-line-segment"

                const coordinates = [prevPoint, currentPoint]

                segment.coordinates = coordinates

                const selectedRange = this.props.selectedRange
                const isSelectedRange = Boolean(
                    selectedRange &&
                        currentPoint.distanceM >= selectedRange.startM &&
                        prevPoint.distanceM <= selectedRange.endM
                )

                if (isSelectedRange) {
                    segment.strokeColor = "#f59e0b"
                } else {
                    switch (currentPoint.type) {
                        case "brake":
                            segment.strokeColor = "#b65656"
                            break
                        case "coast":
                            segment.strokeColor = "#ffff00"
                            break
                        case "acc":
                        default:
                            segment.strokeColor = "#70bd74"
                            break
                    }
                }

                segment.strokeWidth = isSelectedRange ? 12 : 6
                segment.extruded = true
                segment.addEventListener("gmp-click", (event) => {
                    event.stopPropagation()
                    const pointIndex = currentPoint.pointIndex ?? i
                    this.setState({ selectedPointIndex: pointIndex })
                    if (this.props.onPointSelect) {
                        this.props.onPointSelect(pointIndex)
                    }
                })

                this.mapElem.appendChild(segment)
            }
        } catch (error) {
            console.error("Error creating colored segments:", error)
        }
    }

    update3DMarkers() {
        // Clean up existing markers
        this.markerElements.forEach((marker) => {
            if (marker.parentNode) {
                marker.parentNode.removeChild(marker)
            }
        })
        this.markerElements = []

        if (
            this.props.readOnly ||
            !this.props.points ||
            this.props.points.length === 0
        )
            return

        try {
            // Create 3D markers for each point
            this.props.points.forEach((point, index) => {
                const lat = parseFloat(point.lat)
                const lng = parseFloat(point.lng)
                const altitude = parseFloat(point.altitude) || 0

                // Skip invalid points
                if (isNaN(lat) || isNaN(lng)) return

                const marker = document.createElement("gmp-marker-3d")

                marker.position = {
                    lat,
                    lng,
                    altitude: altitude + 2, // Slightly above ground
                }

                // Don't set altitudeMode for now
                // marker.altitudeMode = 'relativeToGround';

                // Style marker based on selection and point type
                if (index === this.state.selectedPointIndex) {
                    marker.scale = 1.5
                    marker.color = "#ffffff"
                } else {
                    marker.scale = 1.0
                    switch (point.type) {
                        case "brake":
                            marker.color = "#b65656"
                            break
                        case "coast":
                            marker.color = "#ffff00"
                            break
                        case "acc":
                        default:
                            marker.color = "#70bd74"
                            break
                    }
                }

                // Add click handling
                marker.addEventListener("gmp-click", (event) => {
                    event.stopPropagation()
                    this.setState({ selectedPointIndex: index })
                    if (this.props.onPointSelect) {
                        this.props.onPointSelect(index)
                    }
                })

                this.mapElem.appendChild(marker)
                this.markerElements.push(marker)
            })

            console.log("3D markers created:", this.markerElements.length)
        } catch (error) {
            console.error("Error creating 3D markers:", error)
        }
    }

    updateCameraPosition() {
        if (this.mapElem) {
            const lat = parseFloat(this.props.lat)
            const lng = parseFloat(this.props.lng)

            if (!isNaN(lat) && !isNaN(lng)) {
                this.mapElem.center = { lat, lng, altitude: 0 }
            }
        }
    }

    updateCameraRange() {
        if (this.mapElem && this.props.zoom) {
            // Convert zoom level to appropriate range for 3D view
            const zoom = parseInt(this.props.zoom)
            const range = Math.max(100, 2000 - zoom * 100)
            this.mapElem.range = range
        }
    }

    // Smooth coordinates using Catmull-Rom spline interpolation
    smoothCoordinates(coordinates) {
        if (coordinates.length < 4) return coordinates

        const smoothed = []
        const resolution = 0.1 // Higher values = more smoothing, lower values = more points

        // Add the first point
        smoothed.push(coordinates[0])

        // Apply Catmull-Rom spline smoothing
        for (let i = 1; i < coordinates.length - 2; i++) {
            const p0 = coordinates[i - 1]
            const p1 = coordinates[i]
            const p2 = coordinates[i + 1]
            const p3 = coordinates[i + 2] || coordinates[i + 1] // Handle end case

            // Add intermediate points between p1 and p2
            for (let t = 0; t <= 1; t += resolution) {
                const lat = this.catmullRom(p0.lat, p1.lat, p2.lat, p3.lat, t)
                const lng = this.catmullRom(p0.lng, p1.lng, p2.lng, p3.lng, t)
                const altitude = this.catmullRom(
                    p0.altitude,
                    p1.altitude,
                    p2.altitude,
                    p3.altitude,
                    t
                )

                smoothed.push({ lat, lng, altitude })
            }
        }

        // Add the last point
        smoothed.push(coordinates[coordinates.length - 1])

        return smoothed
    }

    // Catmull-Rom spline interpolation
    catmullRom(p0, p1, p2, p3, t) {
        const t2 = t * t
        const t3 = t2 * t

        return (
            0.5 *
            (2 * p1 +
                (-p0 + p2) * t +
                (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
                (-p0 + 3 * p1 - 3 * p2 + p3) * t3)
        )
    }

    // Get actual ground level altitude using Google Maps Elevation Service API
    getActualGroundLevel = (callback) => {
        if (!this.props.points || this.props.points.length === 0) {
            console.log("No points available for elevation detection")
            this.groundLevelAltitude = 0
            callback()
            return
        }

        const startPoint = this.props.points[0]
        const lat = parseFloat(startPoint.lat)
        const lng = parseFloat(startPoint.lng)

        if (isNaN(lat) || isNaN(lng)) {
            console.log(
                "Invalid start point coordinates for elevation detection"
            )
            this.groundLevelAltitude = 0
            callback()
            return
        }

        // Use Google Maps Elevation Service to get precise ground level
        if (
            typeof window !== "undefined" &&
            window.google &&
            window.google.maps &&
            window.google.maps.ElevationService
        ) {
            const elevationService = new window.google.maps.ElevationService()
            const location = new window.google.maps.LatLng(lat, lng)

            elevationService.getElevationForLocations(
                {
                    locations: [location],
                },
                (results, status) => {
                    if (
                        status === "OK" &&
                        results &&
                        results[0] &&
                        results[0].elevation !== undefined
                    ) {
                        this.groundLevelAltitude = Math.round(
                            results[0].elevation
                        )
                        console.log(
                            `Ground elevation: ${
                                this.groundLevelAltitude
                            }m at (${lat.toFixed(6)}, ${lng.toFixed(6)})`
                        )
                    } else {
                        console.error(`Elevation service failed: ${status}`)
                        this.groundLevelAltitude = 0
                    }
                    callback()
                }
            )
        } else {
            console.error("Google Maps Elevation Service not available")
            this.groundLevelAltitude = 0
            callback()
        }
    }

    // Get adaptive camera settings based on track type
    getAdaptiveCameraSettings = () => {
        if (!this.props.points || this.props.points.length === 0) {
            return {
                safeAltitude: 200,
                altitudeOffset: 50,
                range: 300,
                isKarting: false,
            }
        }

        // Calculate track length for type detection
        const totalDistance = this.props.points.reduce((dist, point, index) => {
            if (index === 0) return 0
            const prevPoint = this.props.points[index - 1]
            return (
                dist +
                this.calculateDistance(
                    prevPoint.lat,
                    prevPoint.lng,
                    point.lat,
                    point.lng
                )
            )
        }, 0)

        const trackLength = totalDistance / 1000
        const isKartingTrack = trackLength < 2.0

        // Preserve the established karting and racing camera framing.
        const actualGroundLevel = this.groundLevelAltitude || 0
        const safeAltitude = actualGroundLevel + 100

        console.log(
            `Corner navigation - Ground level: ${actualGroundLevel}m, Camera altitude: ${safeAltitude}m, Track: ${
                isKartingTrack ? "Karting" : "Racing"
            }`
        )

        return {
            safeAltitude,
            altitudeOffset: isKartingTrack ? 25 : 50,
            range: isKartingTrack ? 30 : 50,
            isKarting: isKartingTrack,
        }
    }

    getGroundAlignedCameraCenter = (
        point,
        settings,
        tilt = 45,
        heading = 0
    ) => {
        const focusAltitude = settings.safeAltitude + settings.altitudeOffset
        const measuredPointAltitude = Number.parseFloat(point.altitude)
        const groundAltitude = Number.isFinite(measuredPointAltitude)
            ? measuredPointAltitude
            : this.groundLevelAltitude || 0
        const perspectiveOffsetM =
            Math.max(0, focusAltitude - groundAltitude) *
            Math.tan((tilt * Math.PI) / 180)
        const bearing = ((heading + 180) * Math.PI) / 180
        const angularDistance = perspectiveOffsetM / 6371000
        const latitude = (Number(point.lat) * Math.PI) / 180
        const longitude = (Number(point.lng) * Math.PI) / 180
        const targetLatitude = Math.asin(
            Math.sin(latitude) * Math.cos(angularDistance) +
                Math.cos(latitude) *
                    Math.sin(angularDistance) *
                    Math.cos(bearing)
        )
        const targetLongitude =
            longitude +
            Math.atan2(
                Math.sin(bearing) *
                    Math.sin(angularDistance) *
                    Math.cos(latitude),
                Math.cos(angularDistance) -
                    Math.sin(latitude) * Math.sin(targetLatitude)
            )

        return {
            lat: (targetLatitude * 180) / Math.PI,
            lng: (targetLongitude * 180) / Math.PI,
        }
    }

    // Camera animation methods
    flyToPoint = (pointIndex) => {
        if (!this.mapElem || !this.props.points[pointIndex]) return

        const point = this.props.points[pointIndex]
        const settings = this.getAdaptiveCameraSettings()
        const tilt = 45
        const heading = 0
        const cameraCenter = this.getGroundAlignedCameraCenter(
            point,
            settings,
            tilt,
            heading
        )

        this.mapElem.flyCameraTo({
            endCamera: {
                center: {
                    ...cameraCenter,
                    altitude: settings.safeAltitude + settings.altitudeOffset,
                },
                range: settings.range,
                tilt,
                heading,
            },
            durationMillis: 2000,
        })
    }

    flyAroundTrack = () => {
        if (!this.mapElem || this.props.points.length < 4) return

        console.log(
            `Starting smooth track tour with ${this.props.points.length} points`
        )

        // Start the smooth animation
        this.animateSmoothCameraPath()
    }

    calculateHeading = (pointIndex) => {
        if (pointIndex >= this.props.points.length - 1) return 0

        const current = this.props.points[pointIndex]
        const next = this.props.points[pointIndex + 1]

        const deltaLng = next.lng - current.lng
        const deltaLat = next.lat - current.lat

        return Math.atan2(deltaLng, deltaLat) * (180 / Math.PI)
    }

    animateSmoothCameraPath = () => {
        // Create interpolated points for smooth movement
        const interpolatedPoints = this.createInterpolatedPoints(
            this.props.points,
            8
        ) // 8 points between each original point

        // Calculate distances between consecutive points and cumulative distances
        const distances = []
        const cumulativeDistances = [0]
        let totalDistance = 0

        for (let i = 0; i < interpolatedPoints.length; i++) {
            const currentPoint = interpolatedPoints[i]
            const nextPoint =
                interpolatedPoints[(i + 1) % interpolatedPoints.length]

            // Calculate distance in meters using Haversine formula
            const distance = this.calculateDistance(
                currentPoint.lat,
                currentPoint.lng,
                nextPoint.lat,
                nextPoint.lng
            )
            distances.push(distance)
            totalDistance += distance
            cumulativeDistances.push(totalDistance)
        }

        // Use actual ground level from Google Maps Elevation API with original height settings
        const actualGroundLevel = this.groundLevelAltitude || 0
        const safeAltitude = actualGroundLevel + 100 // Original 100m above ground level

        // Detect track type and adjust camera settings accordingly
        const trackLength = totalDistance / 1000 // in kilometers
        const isKartingTrack = trackLength < 2.0 // Karting tracks are typically under 2km

        const cameraAltitudeOffset = isKartingTrack ? 60 : 100 // Lower but safe altitudes for closer viewing
        const cameraRange = isKartingTrack ? 80 : 120 // Much closer for both track types

        console.log(
            `Track type: ${
                isKartingTrack ? "Karting" : "Racing"
            }, Length: ${trackLength.toFixed(2)}km`
        )
        console.log(
            `Track tour - Ground level: ${actualGroundLevel}m, Camera altitude: ${
                safeAltitude + cameraAltitudeOffset
            }m total`
        )
        // Adjust speed based on track type
        const baseSpeed = isKartingTrack ? 80 : 160 // Slower for karting tracks
        const animationDuration =
            (totalDistance / 1000 / (baseSpeed / 3600)) * 1000 // Duration based on total track length

        console.log(
            `Track length: ${(totalDistance / 1000).toFixed(
                2
            )}km, Animation duration: ${(animationDuration / 1000).toFixed(1)}s`
        )

        let startTime = null
        let animationId = null
        let previousHeading = null // Store previous heading for natural smoothing

        const smoothDistanceBasedMove = (timestamp) => {
            if (!this.state.cameraFollowMode) {
                console.log("Smooth track tour stopped")
                if (animationId) {
                    cancelAnimationFrame(animationId)
                }
                return
            }

            if (!startTime) startTime = timestamp

            // Calculate progress through the animation (0 to 1)
            const elapsed = timestamp - startTime
            const progress = (elapsed % animationDuration) / animationDuration

            // Convert progress to distance traveled
            const targetDistance = progress * totalDistance

            // Find which segment we're in based on cumulative distance
            let segmentIndex = 0
            for (let i = 0; i < cumulativeDistances.length - 1; i++) {
                if (
                    targetDistance >= cumulativeDistances[i] &&
                    targetDistance < cumulativeDistances[i + 1]
                ) {
                    segmentIndex = i
                    break
                }
            }

            const currentPoint = interpolatedPoints[segmentIndex]
            const nextPoint =
                interpolatedPoints[
                    (segmentIndex + 1) % interpolatedPoints.length
                ]

            // Calculate interpolation factor within this segment
            const segmentStartDistance = cumulativeDistances[segmentIndex]
            const segmentDistance = distances[segmentIndex]
            const distanceInSegment = targetDistance - segmentStartDistance
            const interpolationFactor =
                segmentDistance > 0 ? distanceInSegment / segmentDistance : 0

            // Smoothly interpolate position
            const smoothLat =
                currentPoint.lat +
                (nextPoint.lat - currentPoint.lat) * interpolationFactor
            const smoothLng =
                currentPoint.lng +
                (nextPoint.lng - currentPoint.lng) * interpolationFactor

            // Note: curvature calculation removed as it's not currently used for speed variation

            // Calculate natural heading - look ahead slightly for anticipation like a real camera operator
            const lookAheadDistance = 2 // Subtle look-ahead for natural anticipation
            const currentHeading = this.calculateHeadingFromPoint(
                currentPoint,
                interpolatedPoints[
                    (segmentIndex + lookAheadDistance) %
                        interpolatedPoints.length
                ]
            )
            const nextHeading = this.calculateHeadingFromPoint(
                nextPoint,
                interpolatedPoints[
                    (segmentIndex + lookAheadDistance + 1) %
                        interpolatedPoints.length
                ]
            )

            // Handle heading wrap-around (e.g., from 359° to 1°)
            let headingDiff = nextHeading - currentHeading
            if (headingDiff > 180) {
                headingDiff -= 360
            } else if (headingDiff < -180) {
                headingDiff += 360
            }

            let smoothHeading =
                currentHeading + headingDiff * interpolationFactor

            // Apply natural, human-like camera smoothing - like a professional camera operator
            if (previousHeading !== null) {
                let headingChangeDiff = smoothHeading - previousHeading

                // Handle wrap-around for heading change
                if (headingChangeDiff > 180) {
                    headingChangeDiff -= 360
                } else if (headingChangeDiff < -180) {
                    headingChangeDiff += 360
                }

                // Natural camera movement - varies based on turn sharpness like a human operator
                const turnSharpness = Math.abs(headingChangeDiff)

                // Adaptive smoothing - gentle on straight sections, more responsive in corners
                let maxChange
                if (turnSharpness < 5) {
                    // Straight sections - very smooth and steady
                    maxChange = 1.5
                } else if (turnSharpness < 15) {
                    // Gentle turns - moderate smoothing with slight lead
                    maxChange = 3.0
                } else {
                    // Sharp corners - more responsive but still smooth
                    maxChange = 5.0
                }

                // Apply natural acceleration/deceleration to camera movement with subtle variation
                const naturalDamping = 0.7 // Natural momentum feeling

                // Add subtle human-like variation (very small, like natural hand tremor)
                const microVariation = Math.sin(timestamp * 0.001) * 0.1 // Tiny natural movement

                if (Math.abs(headingChangeDiff) > maxChange) {
                    const sign = headingChangeDiff > 0 ? 1 : -1
                    smoothHeading =
                        previousHeading +
                        sign * maxChange * naturalDamping +
                        microVariation
                } else {
                    // Small changes - apply gentle momentum with natural variation
                    smoothHeading =
                        previousHeading +
                        headingChangeDiff * naturalDamping +
                        microVariation
                }
            }

            previousHeading = smoothHeading

            // Position camera behind the racing line
            const offsetDistance = 0.0012 // Distance behind the point
            const radians = (smoothHeading - 180) * (Math.PI / 180) // 180 degrees behind

            const cameraLat = smoothLat + Math.cos(radians) * offsetDistance
            const cameraLng = smoothLng + Math.sin(radians) * offsetDistance

            // Directly set camera position for smooth distance-based movement
            if (this.mapElem && this.mapElem.center) {
                this.mapElem.center = {
                    lat: cameraLat,
                    lng: cameraLng,
                    altitude: safeAltitude + cameraAltitudeOffset, // Use highest track point + offset
                }
                this.mapElem.range = cameraRange // Adaptive range based on track type
                this.mapElem.tilt = 60
                this.mapElem.heading = smoothHeading
            }

            // Continue animation
            animationId = requestAnimationFrame(smoothDistanceBasedMove)
        }

        console.log("Starting smooth distance-based camera movement")
        animationId = requestAnimationFrame(smoothDistanceBasedMove)
    }

    // Simplify points using Douglas-Peucker algorithm for smooth camera movement
    simplifyPoints = (points, tolerance = 0.00008) => {
        if (points.length <= 2) return points

        // For very dense data (like iRacing), use adaptive tolerance
        const adaptiveTolerance =
            points.length > 1000 ? tolerance * 2 : tolerance

        return this.douglasPeucker(points, adaptiveTolerance)
    }

    // Douglas-Peucker algorithm implementation
    douglasPeucker = (points, tolerance) => {
        if (points.length <= 2) return points

        // Find the point with maximum distance from line between first and last points
        let maxDistance = 0
        let maxIndex = 0
        const firstPoint = points[0]
        const lastPoint = points[points.length - 1]

        for (let i = 1; i < points.length - 1; i++) {
            const distance = this.perpendicularDistance(
                points[i],
                firstPoint,
                lastPoint
            )
            if (distance > maxDistance) {
                maxDistance = distance
                maxIndex = i
            }
        }

        // If max distance is greater than tolerance, recursively simplify
        if (maxDistance > tolerance) {
            const leftResults = this.douglasPeucker(
                points.slice(0, maxIndex + 1),
                tolerance
            )
            const rightResults = this.douglasPeucker(
                points.slice(maxIndex),
                tolerance
            )

            // Combine results, removing duplicate middle point
            return leftResults.slice(0, -1).concat(rightResults)
        } else {
            // All points between first and last are within tolerance, return just endpoints
            return [firstPoint, lastPoint]
        }
    }

    // Calculate perpendicular distance from point to line
    perpendicularDistance = (point, lineStart, lineEnd) => {
        const dx = lineEnd.lng - lineStart.lng
        const dy = lineEnd.lat - lineStart.lat

        if (dx === 0 && dy === 0) {
            // Line start and end are the same point
            return this.distanceBetweenPoints(point, lineStart)
        }

        const t =
            ((point.lng - lineStart.lng) * dx +
                (point.lat - lineStart.lat) * dy) /
            (dx * dx + dy * dy)

        let closestPoint
        if (t < 0) {
            closestPoint = lineStart
        } else if (t > 1) {
            closestPoint = lineEnd
        } else {
            closestPoint = {
                lat: lineStart.lat + t * dy,
                lng: lineStart.lng + t * dx,
            }
        }

        return this.distanceBetweenPoints(point, closestPoint)
    }

    // Calculate distance between two points in degrees (for simplification algorithm)
    distanceBetweenPoints = (point1, point2) => {
        const dx = point2.lng - point1.lng
        const dy = point2.lat - point1.lat
        return Math.sqrt(dx * dx + dy * dy)
    }

    // Create interpolated points between original racing line points for smoother camera movement
    createInterpolatedPoints = (originalPoints, interpolationCount) => {
        // First, simplify the points if we have too many (like iRacing data)
        let workingPoints = originalPoints
        if (originalPoints.length > 500) {
            console.log(
                `Dense data detected (${originalPoints.length} points), simplifying for smooth camera movement`
            )
            workingPoints = this.simplifyPoints(originalPoints)
            console.log(`Simplified to ${workingPoints.length} points`)
        }

        const interpolated = []

        for (let i = 0; i < workingPoints.length; i++) {
            const currentPoint = workingPoints[i]
            const nextPoint = workingPoints[(i + 1) % workingPoints.length]

            // Add the current point
            interpolated.push(currentPoint)

            // Add interpolated points between current and next
            for (let j = 1; j <= interpolationCount; j++) {
                const t = j / (interpolationCount + 1) // Interpolation factor (0 to 1)

                const interpolatedPoint = {
                    lat:
                        currentPoint.lat +
                        (nextPoint.lat - currentPoint.lat) * t,
                    lng:
                        currentPoint.lng +
                        (nextPoint.lng - currentPoint.lng) * t,
                    altitude:
                        (currentPoint.altitude || 0) +
                        ((nextPoint.altitude || 0) -
                            (currentPoint.altitude || 0)) *
                            t,
                }

                interpolated.push(interpolatedPoint)
            }
        }

        return interpolated
    }

    // Calculate heading between two specific points
    calculateHeadingFromPoint = (point1, point2) => {
        const deltaLat = point2.lat - point1.lat
        const deltaLng = point2.lng - point1.lng

        return Math.atan2(deltaLng, deltaLat) * (180 / Math.PI)
    }

    // Calculate distance between two points using Haversine formula (returns meters)
    calculateDistance = (lat1, lng1, lat2, lng2) => {
        const R = 6371000 // Earth's radius in meters
        const dLat = ((lat2 - lat1) * Math.PI) / 180
        const dLng = ((lng2 - lng1) * Math.PI) / 180
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((lat1 * Math.PI) / 180) *
                Math.cos((lat2 * Math.PI) / 180) *
                Math.sin(dLng / 2) *
                Math.sin(dLng / 2)
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
        return R * c
    }

    // Calculate curvature at a point (0 = straight, 1 = sharp turn)
    calculateCurvature = (pointIndex, points) => {
        const totalPoints = points.length
        const lookAhead = Math.min(3, Math.floor(totalPoints / 20)) // Look ahead/behind 3 points or 5% of track

        const prevIndex = (pointIndex - lookAhead + totalPoints) % totalPoints
        const nextIndex = (pointIndex + lookAhead) % totalPoints

        const prevPoint = points[prevIndex]
        const currentPoint = points[pointIndex]
        const nextPoint = points[nextIndex]

        // Calculate angle change
        const heading1 = this.calculateHeadingFromPoint(prevPoint, currentPoint)
        const heading2 = this.calculateHeadingFromPoint(currentPoint, nextPoint)

        let angleDiff = Math.abs(heading2 - heading1)
        if (angleDiff > 180) {
            angleDiff = 360 - angleDiff
        }

        // Normalize to 0-1 (0 = straight, 1 = 90+ degree turn)
        return Math.min(angleDiff / 90, 1)
    }

    // Find corner points (turns) in the racing line
    findCorners = (points) => {
        // First simplify points for corner detection if we have dense data
        let workingPoints = points
        if (points.length > 500) {
            console.log(
                `Dense data detected for corner detection (${points.length} points), simplifying`
            )
            workingPoints = this.simplifyPoints(points, 0.00012) // Slightly higher tolerance for corner detection
            console.log(
                `Simplified to ${workingPoints.length} points for corner detection`
            )
        }

        const corners = []
        const curvatureThreshold = 0.3 // Slightly lower threshold for better detection
        const minCornerDistance = Math.max(
            3,
            Math.floor(workingPoints.length / 25)
        ) // Adaptive minimum distance

        let lastCornerIndex = -minCornerDistance

        for (let i = 0; i < workingPoints.length; i++) {
            const curvature = this.calculateCurvature(i, workingPoints)

            if (
                curvature > curvatureThreshold &&
                i - lastCornerIndex >= minCornerDistance
            ) {
                corners.push({
                    index: i,
                    point: workingPoints[i],
                    curvature: curvature,
                    cornerNumber: corners.length + 1,
                })
                lastCornerIndex = i
            }
        }

        console.log(
            `Found ${corners.length} corners in track with ${workingPoints.length} simplified points`
        )
        return corners
    }

    // Jump camera to next corner
    jumpToNextCorner = () => {
        if (!this.props.points || this.props.points.length === 0) return

        // Use original points for corner detection (findCorners handles simplification internally)
        const corners = this.findCorners(this.props.points)

        if (corners.length === 0) {
            console.log("No corners found on this track")
            return
        }

        const nextCornerIndex =
            (this.state.currentCornerIndex + 1) % corners.length
        this.setState({ currentCornerIndex: nextCornerIndex })

        const targetCorner = corners[nextCornerIndex]
        const settings = this.getAdaptiveCameraSettings()

        // For simplified points, we need to work with the simplified dataset that was used for corner detection
        let workingPoints = this.props.points
        if (this.props.points.length > 500) {
            workingPoints = this.simplifyPoints(this.props.points, 0.00012)
        }

        // Position camera well back to show braking zone, corner, and exit
        const brakeZoneIndex = Math.max(0, targetCorner.index - 3) // Adjusted for simplified points
        const exitIndex = Math.min(
            workingPoints.length - 1,
            targetCorner.index + 2
        )
        const brakePoint = workingPoints[brakeZoneIndex]
        const exitPoint = workingPoints[exitIndex]

        // Position camera at braking zone, looking through entire corner sequence
        const cameraLat = brakePoint.lat
        const cameraLng = brakePoint.lng
        const cameraHeading = this.calculateHeadingFromPoint(
            brakePoint,
            exitPoint
        )

        console.log(
            `Jumping to corner ${targetCorner.cornerNumber} (${
                nextCornerIndex + 1
            }/${corners.length}) - ${
                settings.isKarting ? "Karting" : "Racing"
            } track - Full corner view`
        )

        this.mapElem.flyCameraTo({
            endCamera: {
                center: {
                    lat: cameraLat,
                    lng: cameraLng,
                    altitude:
                        settings.safeAltitude + (settings.isKarting ? 30 : 40), // Lower altitude for curb detail
                },
                range: settings.isKarting ? 150 : 200, // Further back to see full corner sequence
                tilt: 35, // Lower tilt to see curbs and track surface better
                heading: cameraHeading,
            },
            durationMillis: 1500,
        })
    }

    // Jump camera to previous corner
    jumpToPrevCorner = () => {
        if (!this.props.points || this.props.points.length === 0) return

        // Use original points for corner detection (findCorners handles simplification internally)
        const corners = this.findCorners(this.props.points)

        if (corners.length === 0) {
            console.log("No corners found on this track")
            return
        }

        const prevCornerIndex =
            (this.state.currentCornerIndex - 1 + corners.length) %
            corners.length
        this.setState({ currentCornerIndex: prevCornerIndex })

        const targetCorner = corners[prevCornerIndex]
        const settings = this.getAdaptiveCameraSettings()

        // For simplified points, we need to work with the simplified dataset that was used for corner detection
        let workingPoints = this.props.points
        if (this.props.points.length > 500) {
            workingPoints = this.simplifyPoints(this.props.points, 0.00012)
        }

        // Position camera well back to show braking zone, corner, and exit
        const brakeZoneIndex = Math.max(0, targetCorner.index - 3) // Adjusted for simplified points
        const exitIndex = Math.min(
            workingPoints.length - 1,
            targetCorner.index + 2
        )
        const brakePoint = workingPoints[brakeZoneIndex]
        const exitPoint = workingPoints[exitIndex]

        // Position camera at braking zone, looking through entire corner sequence
        const cameraLat = brakePoint.lat
        const cameraLng = brakePoint.lng
        const cameraHeading = this.calculateHeadingFromPoint(
            brakePoint,
            exitPoint
        )

        console.log(
            `Jumping to corner ${targetCorner.cornerNumber} (${
                prevCornerIndex + 1
            }/${corners.length}) - ${
                settings.isKarting ? "Karting" : "Racing"
            } track - Full corner view`
        )

        this.mapElem.flyCameraTo({
            endCamera: {
                center: {
                    lat: cameraLat,
                    lng: cameraLng,
                    altitude:
                        settings.safeAltitude + (settings.isKarting ? 30 : 40), // Lower altitude for curb detail
                },
                range: settings.isKarting ? 150 : 200, // Further back to see full corner sequence
                tilt: 35, // Lower tilt to see curbs and track surface better
                heading: cameraHeading,
            },
            durationMillis: 1500,
        })
    }

    toggleCameraFollowMode = () => {
        const newMode = !this.state.cameraFollowMode
        console.log(
            `Toggling camera follow mode: ${this.state.cameraFollowMode} -> ${newMode}`
        )
        this.setState({ cameraFollowMode: newMode })

        if (newMode) {
            console.log("Starting track tour...")
            // Small delay to ensure state is updated
            setTimeout(() => {
                this.flyAroundTrack()
            }, 100)
        } else {
            console.log("Stopping track tour...")
        }
    }

    handleDeletePoint = () => {
        const { selectedPointIndex } = this.state
        if (selectedPointIndex === -1) return

        const newPoints = [...this.props.points]
        newPoints.splice(selectedPointIndex, 1)
        this.props.addPoint(newPoints)

        this.setState({ selectedPointIndex: -1 })
        if (this.props.onPointSelect) {
            this.props.onPointSelect(-1)
        }
    }

    render() {
        return (
            <>
                <Script
                    url={`https://maps.googleapis.com/maps/api/js?key=${this.props.apikey}&v=beta&libraries=maps3d,elevation`}
                    onError={(error) => {
                        console.error(
                            "Google Maps 3D API failed to load:",
                            error
                        )
                        this.setState({ mapsJSError: true })
                        if (this.props.onLoadError) {
                            this.props.onLoadError(error)
                        }
                    }}
                    onLoad={() => {
                        console.log("Google Maps 3D API loaded successfully")
                        console.log(
                            "Available Google Maps objects:",
                            Object.keys(window.google?.maps || {})
                        )
                        console.log(
                            "Custom elements available:",
                            !!window.customElements
                        )
                        this.setState({ mapsJSLoaded: true })
                    }}
                />

                <gmp-map-3d
                    ref={this.mapRef}
                    className={
                        this.props.fillContainer
                            ? "h-full w-full"
                            : this.props.embedLayout
                            ? "h-screen w-full"
                            : "h-full w-full"
                    }
                    style={{ display: "block" }}
                />

                {/* 3D Controls */}
                {this.state.map3DInitialized &&
                    (this.props.showControls || !this.props.embedLayout) && (
                        <div className="absolute top-20 right-4 flex flex-col space-y-2 z-10">
                            <button
                                onClick={this.toggleCameraFollowMode}
                                className={`w-32 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    this.state.cameraFollowMode
                                        ? "bg-blue-600 text-white"
                                        : "bg-white/90 text-gray-700 hover:bg-white"
                                }`}
                            >
                                {this.state.cameraFollowMode
                                    ? "Stop Tour"
                                    : "Track Tour"}
                            </button>

                            {this.props.points.length > 0 && (
                                <button
                                    onClick={() => this.flyToPoint(0)}
                                    className="w-32 px-3 py-2 bg-white/90 text-gray-700 hover:bg-white rounded-lg text-sm font-medium transition-colors"
                                >
                                    Start Line
                                </button>
                            )}

                            {/* Corner Navigation */}
                            {this.props.points.length > 0 && (
                                <>
                                    <button
                                        onClick={this.jumpToPrevCorner}
                                        className="w-32 px-3 py-2 bg-white/90 text-gray-700 hover:bg-white rounded-lg text-sm font-medium transition-colors"
                                        title="Previous Corner"
                                    >
                                        Previous Corner
                                    </button>

                                    <button
                                        onClick={this.jumpToNextCorner}
                                        className="w-32 px-3 py-2 bg-white/90 text-gray-700 hover:bg-white rounded-lg text-sm font-medium transition-colors"
                                        title="Next Corner"
                                    >
                                        Next Corner
                                    </button>
                                </>
                            )}

                            <button
                                onClick={() => {
                                    if (this.mapElem) {
                                        this.mapElem.tilt =
                                            this.mapElem.tilt === 0 ? 45 : 0
                                    }
                                }}
                                className="w-32 px-3 py-2 bg-white/90 text-gray-700 hover:bg-white rounded-lg text-sm font-medium transition-colors"
                            >
                                Toggle View
                            </button>
                        </div>
                    )}
            </>
        )
    }
}

export default DynamicMap3D
